import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import {
  Calendar,
  type CalendarProps,
} from '@noorden-insurtech/alba-design-system';
import { DateTime } from 'luxon';
import type { TFieldContext } from '../types';

export type CalendarFieldProps = {
  label: string;
  placeholder?: string;
  required?: boolean;
  minDate?: string | Date;
  maxDate?: string | Date;
  disabled?: boolean;
} & Pick<CalendarProps, 'selectionMode'>;

export function createCalendarField(useFieldContext: () => unknown) {
  return function CalendarField({
    label,
    placeholder,
    required,
    disabled,
    selectionMode = 'single',
    ...props
  }: CalendarFieldProps) {
    const field = useFieldContext() as TFieldContext<string>;

    const maxDate =
      typeof props.maxDate === 'string'
        ? DateTime.fromISO(props.maxDate).toJSDate()
        : props.maxDate;

    const minDate =
      typeof props.minDate === 'string'
        ? DateTime.fromISO(props.minDate).toJSDate()
        : props.minDate;

    const value = field.state.value
      ? [DateTime.fromISO(field.state.value).toJSDate()]
      : undefined;

    const showError =
      field.state.meta.isTouched && field.state.meta.errors.length > 0;

    return (
      <RequiredFieldTooltip active={required}>
        <Calendar
          label={
            <>
              {label}
              {required && <span className='text-alba-error-red-500'>*</span>}
            </>
          }
          value={value}
          onValueChange={value => {
            return field.handleChange(
              value.map(date => date.toISOString().split('T')[0]).join(',')
            );
          }}
          onBlur={field.handleBlur}
          placeholder={placeholder}
          min={minDate}
          max={maxDate}
          disabled={disabled}
          selectionMode={selectionMode}
        >
          {showError && (
            <p className='text-alba-error-red-500 text-xs font-montserrat font-normal leading-tight'>
              {field.state.meta.errors?.[0]}
            </p>
          )}
        </Calendar>
      </RequiredFieldTooltip>
    );
  };
}
